import React, { useEffect, useRef, useState } from 'react';

import { getCodeString } from 'rehype-rewrite';
import { isDiagramSyntaxValid } from '../../helpers/diagramsUtils';

import {
    CODE_BLOCK_CLASS_NAME,
    LANGUAGES_WITH_PREVIEW,
    CODE_BLOCK_PREVIEW_MODE_CLASS_NAME,
} from '../NewRichTextArea/plugins/CodeEditorPlugin/CodeEditorPlugin';
import { COPY_EXCLUDED_ELEMENT_CLASS_NAME } from '../../helpers/handleCopyFormattedText';

import Diagram from '../Diagram/Diagram';
import PlotView from '../PlotView/PlotView';

const STABILIZATION_DELAY = 500;

const CodeRerender = ({ inline, children, className, ...props }) => {
    const languageData = /language-(\w+)/.exec(className || '');
    const language = languageData?.[1];

    let code = '';
    if (!inline && languageData) {
        code = getCodeString(props.node.children);
    }
    const [isCodeStable, setIsCodeStable] = useState(!!code);
    const [isError, setIsError] = useState(() => getInitialErrorState({ code, language }));

    const previousCodeRef = useRef(code);
    const timerRef = useRef(null);

    useEffect(() => {
        if (!LANGUAGES_WITH_PREVIEW.includes(language)) return;

        if (code !== previousCodeRef.current) {
            previousCodeRef.current = code;
            setIsCodeStable(false);
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }

            timerRef.current = setTimeout(async () => {
                setIsCodeStable(true);

                const isError = await validateCode({ language, code });
                setIsError(isError);
            }, STABILIZATION_DELAY);
        }

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [code, language]);

    const showPreview =
        !inline &&
        languageData &&
        LANGUAGES_WITH_PREVIEW.includes(language) &&
        !isError &&
        isCodeStable;

    if (showPreview) {
        const onError = () => setIsError(true);

        const preview = renderPreview({ language, code, onError });

        return (
            <div className={`${CODE_BLOCK_CLASS_NAME} ${CODE_BLOCK_PREVIEW_MODE_CLASS_NAME}`}>
                <div className={COPY_EXCLUDED_ELEMENT_CLASS_NAME}>{preview}</div>

                <pre className="whitespace-pre-wrap hidden">
                    <code className="whitespace-pre-wrap font-body text-body-regular-s text-neutral-500 hidden">
                        {code}
                    </code>
                </pre>
            </div>
        );
    }

    return (
        <code className={className} {...props}>
            {children}
        </code>
    );
};

function getInitialErrorState({ code, language }) {
    if (!code) return true;
    if (!LANGUAGES_WITH_PREVIEW.includes(language)) return false;

    if (language === 'plotly') {
        try {
            JSON.parse(code);
            return false;
        } catch {
            return true;
        }
    }

    return false;
}

async function validateCode({ language, code }) {
    if (language === 'plotly') {
        try {
            JSON.parse(code);
            return false;
        } catch {
            return true;
        }
    }
    if (language === 'mermaid') {
        const isValid = await isDiagramSyntaxValid(code);
        return !isValid;
    }
    return false;
}

function renderPreview({ language, code, onError }) {
    switch (language) {
        case 'plotly':
            return (
                <PlotView
                    value={code}
                    outlined
                    onError={onError}
                    customClassName="!border-neutral-200"
                />
            );
        case 'mermaid':
            return (
                <Diagram
                    code={code}
                    name="Diagram Preview"
                    withActionButtons={false}
                    onError={onError}
                    customClassName="hide-default-error-banner"
                />
            );
        default:
            return null;
    }
}

export const customCodeRenderer = {
    code: CodeRerender,
};
